import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Check, AlertCircle, Loader2, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type ActionStatus =
  | { type: "idle" }
  | { type: "confirming"; message: string; onConfirm: () => void; onCancel: () => void }
  | { type: "loading"; message: string }
  | { type: "success"; message: string }
  | { type: "error"; message: string };

export type SetActionStatus = (status: ActionStatus) => void;

/**
 * Atajo de teclado para una accion masiva.
 *
 * Forma simple (legacy): un string con el nombre del key (`"d"`, `"x"`).
 * Soporta mayusculas — el listener matchea `e.key.toLowerCase()` contra
 * `shortcut.toLowerCase()` y exige que NO haya modificadores.
 *
 * Forma extendida: un `ShortcutSpec` con:
 *   - `keys`: lista de teclas que disparan la accion (cualquiera matchea).
 *     Usar nombres canónicos: `"Backspace"`, `"Delete"`, `"Enter"`,
 *     `"Escape"`, o letras `"a"`-`"z"`.
 *   - `shift`: si true, la accion solo se dispara con Shift presionado.
 *     Default false.
 *   - `label`: texto a mostrar en el `<kbd>` y en el tooltip.
 *
 * Ejemplos:
 *   shortcut: "x"                            → X (sin modificadores)
 *   shortcut: { keys: ["Delete"], shift: true, label: "Shift+Del" }
 */
export type BulkActionShortcut =
  | string
  | { keys: string[]; shift?: boolean; label: string };

export interface BulkAction<TData = unknown> {
  label: string;
  icon?: LucideIcon;
  onClick: (selectedIds: string[], setStatus: SetActionStatus) => void;
  variant?: "default" | "secondary" | "outline" | "ghost" | "destructive";
  shortcut?: BulkActionShortcut;
  /**
   * Gate de habilitacion por seleccion completa. Si retorna false, el boton
   * se deshabilita y se muestra el motivo en el tooltip. Default: siempre
   * habilitado. Se evalua contra el array completo de IDs seleccionados.
   * Recibe tambien las filas correspondientes (de la pagina actual) para
   * permitir validar status o relaciones antes de habilitar la accion.
   */
  condition?: (
    selectedIds: string[],
    selectionRows: TData[],
  ) => boolean | { enabled: boolean; reason?: string };
}

// Normaliza un shortcut a la forma extendida. Strings simples se envuelven en
// un spec sin modificadores (preserva el comportamiento legacy: matchea la
// tecla sola, label = la tecla en mayusculas).
function normalizeShortcut(
  shortcut: BulkActionShortcut,
): { keys: string[]; shift: boolean; label: string; matchKey: (e: KeyboardEvent) => boolean } {
  if (typeof shortcut === "string") {
    const k = shortcut.toLowerCase();
    return {
      keys: [k],
      shift: false,
      label: shortcut.toUpperCase(),
      matchKey: (e) => !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === k,
    };
  }
  return {
    keys: shortcut.keys.map((k) => k.toLowerCase()),
    shift: shortcut.shift ?? false,
    label: shortcut.label,
    matchKey: (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return false;
      if ((shortcut.shift ?? false) !== e.shiftKey) return false;
      return shortcut.keys.some((k) => e.key.toLowerCase() === k.toLowerCase());
    },
  };
}

interface BulkActionBarProps<TData = unknown> {
  selectedRows: Set<string>;
  onClearSelection: () => void;
  actions: BulkAction<TData>[];
  label?: string;
  /**
   * Filas correspondientes a los IDs seleccionados. Se pasa a `condition`
   * para que las acciones bulk puedan validar status/relaciones antes de
   * habilitarse.
   */
  selectionRows?: TData[];
}

// ─── Animated content slot ───────────────────────────────────────────────────
// Holds the previous content during exit, swaps to new content after exit anim.
function useAnimatedStatus(status: ActionStatus, duration = 150) {
  const [displayed, setDisplayed] = useState(status);
  const [exiting, setExiting] = useState(false);
  const pendingRef = useRef<ActionStatus | null>(null);

  useEffect(() => {
    if (status.type === displayed.type) {
      // same type — update in place (e.g. message change) without animation
      setDisplayed(status);
      return;
    }

    pendingRef.current = status;
    setExiting(true);
    const t = setTimeout(() => {
      if (pendingRef.current) setDisplayed(pendingRef.current);
      setExiting(false);
      pendingRef.current = null;
    }, duration);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.type]);

  return { displayed, exiting };
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function BulkActionBar<TData = unknown>({
  selectedRows,
  onClearSelection,
  actions,
  label = "seleccionadas",
  selectionRows = [],
}: BulkActionBarProps<TData>) {
  const count = selectedRows.size;
  const selectedIds = Array.from(selectedRows);
  const barRef = useRef<HTMLDivElement>(null);
  const lastCountRef = useRef(count);

  const [visible, setVisible] = useState(count > 0);
  const [barLeaving, setBarLeaving] = useState(false);
  const [status, setStatus] = useState<ActionStatus>({ type: "idle" });

  const { displayed, exiting } = useAnimatedStatus(status);

  // Bar visibility
  useEffect(() => {
    if (count > 0) {
      lastCountRef.current = count;
      setBarLeaving(false);
      setVisible(true);
    } else if (visible && status.type !== "loading" && status.type !== "confirming") {
      setBarLeaving(true);
      const t = setTimeout(() => {
        setVisible(false);
        setBarLeaving(false);
        setStatus({ type: "idle" });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [count, visible, status.type]);

  // Auto-clear selection after success
  useEffect(() => {
    if (status.type !== "success") return;
    const t = setTimeout(() => onClearSelection(), 1200);
    return () => clearTimeout(t);
  }, [status.type, onClearSelection]);

  const handleAction = useCallback(
    (action: BulkAction<TData>) => {
      if (status.type === "loading" || status.type === "confirming") return;
      action.onClick(selectedIds, setStatus);
    },
    [status.type, selectedIds],
  );

  // Normaliza los shortcuts una sola vez por render; el listener captura
  // los specs por closure, evitando reconstruir matchers en cada keydown.
  const normalizedShortcuts = useMemo(
    () => actions.map((a) => (a.shortcut ? normalizeShortcut(a.shortcut) : null)),
    [actions],
  );

  // Keyboard shortcuts
  useEffect(() => {
    if (count === 0) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      const inEditable =
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
      // Si otro listener más prioritario (capture phase) ya marcó el evento
      // como manejado — p.ej. salir de edición de una celda con Esc, o el
      // handler de captura de la sección de items para Shift+Del — no
      // pisamos su acción.
      if (e.defaultPrevented) return;
      if (inEditable) return;
      const key = e.key.toLowerCase();
      if (key === "escape") {
        if (status.type === "confirming") status.onCancel();
        else if (status.type !== "loading") onClearSelection();
        return;
      }
      if (status.type === "confirming" && (key === "enter" || key === "return")) {
        status.onConfirm();
        return;
      }
      for (let i = 0; i < actions.length; i++) {
        const spec = normalizedShortcuts[i];
        if (spec && spec.matchKey(e)) {
          e.preventDefault();
          handleAction(actions[i]);
          return;
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [count, actions, normalizedShortcuts, handleAction, onClearSelection, status]);

  if (!visible) return null;

  const contentClass = cn(
    "flex items-center gap-2 transition-all duration-150",
    exiting ? "opacity-0 scale-95" : "opacity-100 scale-100",
  );

  const content = (
    <ActionBarContent
      status={displayed}
      actions={actions}
      selectedIds={selectedIds}
      selectionRows={selectionRows}
      onClearSelection={onClearSelection}
      onAction={handleAction}
      label={label}
      count={lastCountRef.current}
      contentClass={contentClass}
      onErrorRetry={() => setStatus({ type: "idle" })}
    />
  );

  const bar = (
    <div
      className="fixed left-1/2 z-[140] -translate-x-1/2 bottom-[calc(3.5rem+1.5rem)] md:bottom-6"
      style={{ pointerEvents: "none" }}
    >
      <TooltipProvider>
        <div
          ref={barRef}
          style={{ pointerEvents: "auto" }}
          className={cn(
            "flex items-center gap-1.5 rounded-xl border border-border bg-background px-3 py-1.5 shadow-lg transition-[width] duration-150",
            barLeaving
              ? "animate-out fade-out slide-out-to-bottom-3 duration-200"
              : "animate-in fade-in slide-in-from-bottom-3 duration-200",
          )}
        >
          {content}
        </div>
      </TooltipProvider>
    </div>
  );

  return createPortal(bar, document.body);
}

// ─── Inner content (animada por estado) ───────────────────────────────────────
// Reutilizada por BulkActionBar y por barras inline (ej. confirmar
// eliminación por fila). Espera que el padre provea los callbacks de acción.
export interface ActionBarContentProps<TData = unknown> {
  status: ActionStatus;
  actions: BulkAction<TData>[];
  selectedIds: string[];
  selectionRows?: TData[];
  onClearSelection?: () => void;
  onAction: (action: BulkAction<TData>) => void;
  label?: string;
  count?: number;
  contentClass?: string;
  /** Etiqueta del counter cuando se muestra. Default: usa `label`. */
  countLabel?: string;
  /** Callback para el botón Reintentar del estado `error`. */
  onErrorRetry?: () => void;
  /** Si true, el botón Confirmar recibe el foco al entrar en `confirming`.
   * Default: false (BulkActionBar no enfoca para evitar Enter accidental). */
  autoFocusConfirm?: boolean;
}

export function ActionBarContent<TData = unknown>({
  status,
  actions,
  selectedIds,
  selectionRows = [],
  onClearSelection,
  onAction,
  label = "seleccionadas",
  count,
  contentClass,
  countLabel,
  onErrorRetry,
  autoFocusConfirm = false,
}: ActionBarContentProps<TData>) {
  if (status.type === "loading") {
    return (
      <>
        <Loader2 className="h-3 w-3 animate-spin shrink-0" />
        <span className="text-xs font-medium">{status.message}</span>
      </>
    );
  }
  if (status.type === "success") {
    return (
      <>
        <Check className="h-3 w-3 shrink-0 text-green-600 dark:text-green-400" />
        <span className="text-xs font-medium text-green-600 dark:text-green-400">{status.message}</span>
      </>
    );
  }
  if (status.type === "error") {
    return (
      <>
        <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />
        <span className="text-xs font-medium text-destructive">{status.message}</span>
        <button
          onClick={onErrorRetry}
          className="text-xs underline underline-offset-2 hover:no-underline ml-1 text-destructive"
        >
          Reintentar
        </button>
      </>
    );
  }
  if (status.type === "confirming") {
    return (
      <>
        <span className="text-xs text-muted-foreground">{status.message}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={status.onCancel}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="h-7 text-xs"
            onClick={status.onConfirm}
            autoFocus={autoFocusConfirm}
          >
            Confirmar
          </Button>
        </div>
      </>
    );
  }

  // idle: acciones
  return (
    <div className={cn("flex items-center gap-2", contentClass)}>
      {onClearSelection && (
        <button
          onClick={onClearSelection}
          className="flex items-center gap-1.5 text-xs font-medium text-foreground mr-1 hover:text-muted-foreground transition-colors"
        >
          <X className="h-3.5 w-3.5 shrink-0" />
          <span className="font-semibold tabular-nums">{count ?? selectedIds.length}</span>
          <span className="text-muted-foreground">{countLabel ?? label}</span>
        </button>
      )}
      {onClearSelection && <div className="w-px h-5 bg-border mx-1" />}
      <div className="flex items-center gap-2">
        {actions.map((action, index) => {
          const Icon = action.icon;
          const gate: { enabled: boolean; reason?: string } = action.condition
            ? (() => {
                const g = action.condition!(selectedIds, selectionRows);
                return typeof g === "boolean"
                  ? { enabled: g }
                  : { enabled: g.enabled, reason: g.reason };
              })()
            : { enabled: true };
          const shortcutLabel = action.shortcut
            ? typeof action.shortcut === "string"
              ? action.shortcut.toUpperCase()
              : action.shortcut.label
            : null;
          const tooltipText =
            !gate.enabled && gate.reason
              ? gate.reason
              : shortcutLabel
                ? `${action.label} (${shortcutLabel})`
                : action.label;
          return (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <Button
                  variant={action.variant ?? "outline"}
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs"
                  disabled={!gate.enabled}
                  onClick={() => onAction(action)}
                >
                  {Icon && <Icon className="h-3 w-3" />}
                  <span>{action.label}</span>
                  {shortcutLabel && (
                    <kbd className="pointer-events-none ml-1 hidden h-5 select-none items-center rounded bg-current/10 px-1.5 font-mono text-[10px] font-medium leading-none sm:flex">
                      {shortcutLabel}
                    </kbd>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
